import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HelmBadge, HelmMarkCompact } from "@/components/helm/HelmMark";
import { OpenAlerts, etTime } from "@/components/helm/OpenAlerts";
import {
  LayoutDashboard, Users, FolderLock, Bot, Settings, ClipboardList, Network, LogOut, Menu, X, Building2, BookMarked, MessageSquare, BarChart3,
} from "lucide-react";

const NAV = [
  { to: "/agent", label: "Action Dashboard", icon: LayoutDashboard, end: true },
  { to: "/agent/households", label: "My Households", icon: Users },
  { to: "/agent/messages", label: "Messages", icon: MessageSquare, unread: true },
  { to: "/agent/my-book", label: "My Book", icon: BookMarked },
  { to: "/agent/worklog", label: "Work Log", icon: ClipboardList },
  { to: "/agent/roster", label: "Roster", icon: Network, ownerOnly: true },
  { to: "/agent/agency", label: "Agency Overview", icon: Building2, ownerOnly: true },
  { to: "/agent/funnel", label: "SBA Ad Funnel", icon: BarChart3 },
  { to: "/agent/documents", label: "Documents", icon: FolderLock },
  { to: "/agent/ask", label: "Ask Nauticus", icon: Bot },
  { to: "/agent/settings", label: "Identity & Settings", icon: Settings },
];

export function HelmShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [agencyName, setAgencyName] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const isOwner = user?.role === "platform_admin";
  const items = NAV.filter((i) => !i.ownerOnly || isOwner);
  const [unread, setUnread] = useState(0);
  const notified = useRef(null);
  const [opens, setOpens] = useState([]);
  const openCase = async (a) => {
    try { await api.post(`/agent/compare-shares/${a.token}/alert-read`); } catch { /* best-effort */ }
    setOpens((l) => l.filter((x) => x.token !== a.token));
    setOpen(false);
    navigate(`/agent/ask?case=${encodeURIComponent(a.case_id)}`);
  };

  // In-app reply alerts: polls while Helm is open in a tab (no push/email/background alerts).
  useEffect(() => {
    const LS = `sms-notified-${user?.id}`;
    if (notified.current === null) {
      try { notified.current = new Set(JSON.parse(localStorage.getItem(LS) || "[]")); } catch { notified.current = new Set(); }
    }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/sms/unread");
        if (stop) return;
        setUnread(data.count || 0);
        (data.latest || []).filter((m) => !notified.current.has(m.id)).reverse().forEach((m) => {
          notified.current.add(m.id);
          toast(`New text from ${m.household_name || m.from_number}`, {
            description: (m.body || "").slice(0, 120), duration: 15000,
            action: { label: "Open", onClick: () => navigate(`/agent/messages?thread=${encodeURIComponent(m.thread_key)}`) },
          });
        });
        localStorage.setItem(LS, JSON.stringify([...notified.current].slice(-200)));
      } catch { /* alerts are best-effort chrome */ }
      try {
        const { data } = await api.get("/agent/compare-shares/open-alerts");
        if (stop) return;
        setOpens(data.alerts || []);
        (data.alerts || []).filter((a) => !notified.current.has(`open-${a.token}`)).reverse().forEach((a) => {
          notified.current.add(`open-${a.token}`);
          toast(`Comparison opened by ${a.recipient_label}`, {
            description: etTime(a.first_opened_at), duration: 15000,
            action: { label: "Open comparison", onClick: () => openCase(a) },
          });
        });
        localStorage.setItem(LS, JSON.stringify([...notified.current].slice(-200)));
      } catch { /* alerts are best-effort chrome */ }
    };
    poll();
    const t = setInterval(poll, 20000);
    window.addEventListener("sms-unread-refresh", poll);
    return () => { stop = true; clearInterval(t); window.removeEventListener("sms-unread-refresh", poll); };
  }, [user?.id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let revoked = [];
    (async () => {
      try {
        const { data } = await api.get("/agent/identity");
        setAgencyName(data?.agency?.name || "");
        if (data?.agent?.has_photo && user?.id) {
          const r = await api.get(`/agent/identity/${user.id}/photo`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data); revoked.push(u); setPhotoUrl(u);
        }
        if (data?.agency?.has_logo) {
          const r = await api.get(`/agent/agency/logo`, { responseType: "blob" });
          const u = URL.createObjectURL(r.data); revoked.push(u); setLogoUrl(u);
        }
      } catch { /* identity is optional chrome */ }
    })();
    return () => revoked.forEach((u) => URL.revokeObjectURL(u));
  }, [user?.id]);

  const isActive = (item) => (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to));
  const initials = (user?.name || "A").split(" ").map((p) => p[0]).slice(0, 2).join("");
  const handleLogout = async () => { await logout(); navigate("/login"); };
  const subtitle = agencyName ? `${agencyName.toUpperCase()} · AGENT WORKSPACE` : "NAUTICUS · AGENT WORKSPACE";

  const Brand = () => (
    <div className="flex items-center gap-3">
      <HelmBadge variant="light" subtitle={subtitle} />
      {logoUrl && <img src={logoUrl} alt="" className="h-7 max-w-[72px] object-contain ml-1" data-testid="helm-agency-logo" />}
    </div>
  );

  const NavLinks = () => (
    <nav className="flex-1 px-3 py-5 space-y-1" data-testid="helm-nav">
      {items.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
          <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
            data-testid={`helm-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
              active ? "bg-[#D4AF37] text-[#0B192C]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
            <Icon className="w-[18px] h-[18px] shrink-0" />
            {item.label}
            {item.unread && unread > 0 && <span className="ml-auto text-[10px] font-bold min-w-[20px] text-center px-1.5 py-0.5 rounded-full bg-[#B91C1C] text-white" data-testid="helm-nav-messages-unread">{unread}</span>}
            {item.ownerOnly && <span className="ml-auto text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-amber-300">Owner</span>}
          </Link>
        );
      })}
    </nav>
  );

  const AgentFooter = () => (
    <div className="px-4 py-5 border-t border-slate-800" data-testid="helm-agent-identity">
      <div className="flex items-center gap-3 px-1 mb-3">
        <div className="w-9 h-9 rounded-full bg-[#D4AF37] text-[#0B192C] flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden">
          {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="helm-agent-photo" /> : initials}
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-semibold truncate">{user?.name || "Agent"}</p>
          <p className="text-slate-400 text-xs capitalize truncate">{isOwner ? "Agency Owner" : "Licensed Advisor"}</p>
        </div>
      </div>
      <Button variant="ghost" onClick={handleLogout} data-testid="helm-logout"
        className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5">
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#0B192C] flex-col justify-between border-r border-slate-800" data-testid="helm-sidebar">
        <div>
          <div className="px-5 py-6 border-b border-slate-800"><Brand /></div>
          <NavLinks />
          <OpenAlerts alerts={opens} onOpen={openCase} />
        </div>
        <AgentFooter />
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} data-testid="helm-drawer-overlay" />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#0B192C] flex flex-col justify-between lg:hidden transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="helm-mobile-drawer">
        <div>
          <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
            <Brand />
            <button onClick={() => setOpen(false)} className="text-slate-300" data-testid="helm-drawer-close"><X className="w-5 h-5" /></button>
          </div>
          <NavLinks />
          <OpenAlerts alerts={opens} onOpen={openCase} />
        </div>
        <AgentFooter />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 bg-[#0B192C] px-4 py-3 flex items-center justify-between border-b border-slate-800" data-testid="helm-mobile-header">
          <HelmMarkCompact variant="light" />
          <button onClick={() => setOpen(true)} className="text-white relative" data-testid="helm-open-drawer"><Menu className="w-6 h-6" />
            {unread + opens.length > 0 && <span className="absolute -top-1 -right-1 text-[9px] font-bold px-1 rounded-full bg-[#B91C1C] text-white" data-testid="helm-mobile-unread">{unread + opens.length}</span>}
          </button>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
